import { SyntheticEvent } from "react";
import { Action, Store } from "redux";
import { SagaMiddleware, Task } from "redux-saga";

import { IDataReaderClient } from "@multiweb/clients/dataReader";
import {
    IAttrValue,
    ILanguageExtended,
    ILocalizationItem,
    IPublicAttributeInfo,
    IRunMethodResult,
    OWDataType,
} from "@multiweb/types/contracts";
import {
    CancellationToken,
    ConditionOperator,
    DateTimeFormatObject,
    EventCallback,
    ExperimentName,
    ExtendedPropertiesType,
    GetFormattedNumberStringParams,
    IAppConfiguration,
    IBigNumber,
    IDataEventsManager,
    IDefferredWindow,
    IEnumType,
    IMenuNavigationConfig,
    INumberFormatInfo,
    INumberFormatOptions,
    IOWObject,
    IOWObjectList,
    IParticipant,
    ITypeFactory,
    IVisible,
    IWorkflowMenuItem,
    RequestContext,
    RunStaticMethodParams,
    RunStaticMethodWithParamsConfig,
} from "@multiweb/types/multiweb";
import { ApplicationModule, IAppState, ObjectType, OpenEditWindowParams, ReactAction, ReactActionId, SagaWithReturnType } from "@multiweb/types/react";
import { Tail } from "@redux-saga/core/effects";
import { NAME } from "./constants";
import { FileNameParts, ICryptographicInfo, ISignatureInfo } from "./types";

/**
 * Definuje rozhraní bridge modulu (bez akcí, ty jsou exportovány samostatně).
 * Bridge modul slouží pro komunikaci mezi původní částí aplikace a reactí částí.
 * Podrobnější informace o bridge modulu je možné najít v readme.md a ve Storybooku https://teaf/mw/storybook/?path=/story/dokumentace-moduly-core-bridge--dokumentace.
 *
 * Rozhraní je složené z menších dílčích rozhraní, které seskupují funkcionality podle oblasti.
 *
 * Metody tohoto rozhraní jsou často volané v ságách. Pro jednoduchost použití a unit testů je žádoucí,
 * aby byly metody využívající ve své implementaci "this" bindovány k instanci své třídy.
 * Doporučený postup je použití "property initializer syntax" a arrow funkcí.
 */
export interface IBridge extends
    IBridgeCore,
    IBridgeCommons,
    IBridgeData,
    IBridgeUI,
    IBridgeFormatting,
    IBridgeLocalization,
    IBridgeFrames,
    IBridgeMainMenu,
    IBridgeFile,
    IBridgeCrypto { }

/**
 * Definuje základní funkcionalitu bridge modulu, která je nutná pro komunikaci mezi
 * původní částí aplikace a reactí částí aplikace. Jedná se o funkce pro inicializaci bridge modulu
 * a funkce pro podporu konektorů, které napojují konkrétní komponenty do původní aplikace.
 *
 * Více o konektorech ve Storybooku: https://teaf.tescosw.cz/mw/storybook/?path=/story/dokumentace-komponenty--2-konektory
 */
export interface IBridgeCore {

    /**
     * Obsahuje popis bridge modulu pro účely zapojení modulu do aplikace.
     * Určeno pouze pro použití v setup modulu, nebo v ProxyBridge.
     */
    readonly module: ApplicationModule<typeof NAME>;

    /**
     * Nastaví redux store pro bridge modul. Store se využívá pro vysílání akcí. Před použitím bridge modulu
     * je nutné nastavit store pomocí této metody. Store nemůže být importována přímo kvůli cyklickým závislostem.
     *
     * Funkce pro spuštění ságy umožňuje zapojit v Connector třídách dynamicky ságu. Díky tomu může Connector reagovat na akce z reduxu.
     *
     * @param storeParam Redux store pro vysílání akcí.
     * @param runSaga Funkce z redux saga middleware pro spouštění ság.
     */
    setStore(storeParam: Store<IAppState>, runSaga: SagaMiddleware["run"]): void;

    /**
     * Zajistí vhodné zpracování chyby.
     *
     * Typicky se jedná o zobrazení chybového dialogu pomocí odpovídající manager třídy z původní části aplikace.
     *
     * @param error Odchycená chyba pro zpracování.
     */
    errorHandler(error: unknown): void;

    /**
     * Slouží pro komunikaci směrem z původní aplikace k Reactí části aplikace.
     * S Reactí částí se vždy komunikuje pomocí akcí. Tato metoda umožňuje vysílat akce.
     *
     * Před použitím musí být inicializovaná store.
     *
     * @param action Akce se vytvářejí pomocí action creator funkcí, které jsou k dispozici v rámci bridge modulu.
     * @example
     * import { StoreManager } from "@multiweb/reactSetup";
     * import { bridge } from "@multiweb/reactCore/bridge";
     * const { dispatchAction } = bridge;
     * StoreManager.initializeStore(!!AppContext.instance.configuration.developmentMode);
     * dispatchAction(createExampleAction(arg1, arg2));
     */
    dispatchAction(action: Action): void;

    /**
     * Spustí zadanou ságu a předá ji případné parametry. Slouží pro naslouchání akcí a získávání dat z Reactí části pro původní aplikaci.
     *
     * Před použitím musí být inicializovaná store.
     * @example
     * import { StoreManager } from "@multiweb/reactSetup";
     * import { bridge } from "@multiweb/reactCore/bridge";
     * const { runSaga } = bridge;
     * StoreManager.initializeStore(!!AppContext.instance.configuration.developmentMode);
     * runSaga(saga, arg1, arg2);
     */
    runSaga<S extends SagaWithReturnType<any[]>>(saga: S, ...args: Parameters<S>): Task;

    /**
     * Slouží pro čtení dat původní aplikací z Reactí části aplikace.
     * Tato metoda umožňuje čtení stavu ze store. Příkladem využití může být např. selektor.
     *
     * Před použitím musí být inicializovaná store.
     *
     * @example
     * import { StoreManager } from "@multiweb/reactSetup";
     * import { bridge } from "@multiweb/reactCore/bridge";
     * const { getState } = bridge";
     * import { getValue } from "./selectors"; // ukázka cesty
     * StoreManager.initializeStore(!!AppContext.instance.configuration.developmentMode);
     * const state = getState();
     * getValue(state, controlsKey);
     */
    getState(): IAppState;

    /**
     * Slouží pro zavolání selektoru v nereactí části aplikace.
     * @param selector Funkce selektoru
     * @param args Argumenty selektoru
     */
    useSelector<Fn extends (state: IAppState, ...args: Tail<Parameters<Fn>>) => ReturnType<Fn>>(
        selector: Fn,
        ...args: Tail<Parameters<Fn>>
    ): ReturnType<Fn>;

    /**
     * Získá DIContainer. Určeno primárně pro použití pro customizaci react component.
     * V jiných místech by se nemělo využívat.
     */
    getDIContainer(): ITypeFactory;
}

/**
 * Definuje obecné funkce z multiwebu, které nepatří do žádné konkrétní oblasti a hodí se i pro použití v react části.
 */
export interface IBridgeCommons {

    /**
     * Vygeneruje univerzální unikátní identifikátor.
     */
    generateUUID(): string;

    /**
     * Vrací konfigurační nastavení aplikace.
     */
    getAppConfiguration(): IAppConfiguration;

    /**
     * Zjistí, zda je předaná experimentální funkcionalita aktivována.
     * @see https://www.martinfowler.com/articles/feature-toggles.html Feature Toggles
     *
     * Výsledek funkce je stálý!
     * - Resp. Není možné jej změnit za běhu aplikace.
     * - Hodnota je závislá na konfiguraci, která se načte jednorázově při startu aplikace.
     * - Proto je možné tuto funkci využít i v react komponentách.
     *
     * @param experimentName Název funkcionality
     */
    isExperimentEnabled(experimentName: ExperimentName): boolean;

    /**
     * Zjistí, zda je akce akcí signalizující načtení hodnoty určitého klíče z local storage.
     * @param key Klíč v local storage.
     */
    createIsLocalStorageKeyLoadedActionPattern(key: string): (action: Action) => boolean;

    /**
     * Vytvoří instanci třídy BigNumberProxy.
     * @param value Hodnota.
     */
    createBigNumberProxy(value: string | number): IBigNumber;

    /**
     * Otevře danou URL adresu
     * @param url URL adresa
     * @param target Specifikace cíle otevření
     */
    openUrl(url: string, target?: string): void;
}

/**
 * Definuje rozhraní pro načítání různých dat a práci s atributy.
 */
export interface IBridgeData {
    /**
     * Načte seznam hráčů nad vybraným aktivním workflow
     *
     * @param instanceId identifikátor instance objektu
     * @param activityId identifikátor aktivity
     * @param requestContext Kontext, v rámci kterého bude požadavek vykonán. (Role uživatele, atd.)
     */
    getActivityParticipants(instanceId: string, objectList: IOWObjectList, activityId: string, requestContext?: RequestContext): Promise<IParticipant[]>;

    /**
     * Vytvoří instanci třídy **AttrValue** se zadanou hodnotou.
     * @param value Hodnota
     */
    getAttrValue(value: any): IAttrValue;

    /**
     * Vrací informace o atributech přiřazených k dané třídě
     * @param objectName Název třídy
     * @param attributesToCheck Seznam atributu
     */
    getAttributeInfo(objectName: string, attributesToCheck: string[]): Promise<readonly IPublicAttributeInfo[]>;

    /** @see AttrValueReader.read */
    readAttributeValue(attrValue: IAttrValue | ObjectType | null | undefined, explicitKnownTypes?: any[]): any;

    /** Zkombinuje pole podmínek */
    combineConditionsArray(operator: ConditionOperator, conditions: string[]): string;

    /**
     * Získá klienta pro čtení dat.
     */
    getDataReaderClient(): IDataReaderClient;

    /**
     * Získá url pro stažení souboru z DMS
     * @param dataObjectId Jednoznačný identifikátor objektu.
     * @param objectName Název třídy objektu
     * @param attributeName Název atributu, který obsahuje odkaz na dokument uložený v DMS
     */
    getDocumentDownloadUrlByObjectId(dataObjectId: string, objectName: string, attributeName: string): Promise<string | null>;

    /**
     * Spustí metodu na dané třídě
     * @param config Konfigurační objekt
     * @param cancellationToken Token k přerušení operace
     */
    runStaticMethod(config: RunStaticMethodParams, cancellationToken?: CancellationToken): Promise<IRunMethodResult>;

    /**
     * Spustí metodu na dané třídě s parametry
     * @param config Konfigurační objekt
     * @param cancellationToken Token k přerušení operace
     */
    runStaticMethodWithParams(config: RunStaticMethodWithParamsConfig, cancellationToken?: CancellationToken): Promise<IRunMethodResult>;

    /**
     * Vytváří prázdný OWObjectList
     */
    createObjectList(): IOWObjectList;

    /**
     * Jestli je Workflow definováno pro specifickou třídu, vrací true.
     *
     * @param className Název třídy, která se má zkontrolovat.
     * @param requestContext Viz {@link RequestContext}
     * @param force Ignorování cache
     * @return True, když Workflow je definováno pro specifickou třídu, jinak false.
     */
    isWFDefinedToClass(className: string, requestContext?: RequestContext, force?: boolean): Promise<boolean>;

    /**
     * Vrací seznam spouštěčů typu Menu pro seznam objektů
     * Používá se pro konstrukci WF menu při hromadné editaci: průnik spouštěčů za objekty
     * @param objectList Seznam objektů, pro které se bude hledat průnik spouštěčů
     * @param requestContext Kontext, v rámci kterého bude požadavek vykonán. (Role uživatele, atd.)
     * @return Seznam workflowmenu objektů
    */
    objectsWFMenu(objectList: IOWObjectList, requestContext?: RequestContext): Promise<IWorkflowMenuItem[]>;

    /**
     * Získá zaregistrovanou instanci aplikačního objektu nebo null, pokud takový zaregitrovaný není
     * @param instanceId Identifikátor instance aplikačního objektu
     */
    getInstance(instanceId: string): IOWObject | null;

    /**
     * Spustí přípravu stahování souboru a zajistí, že po jejím doběhnutí je už nachystaný nebo došlo ke zrušení stahování.
     * V chybových situacích informuje uživatele oknem s chybovou hláškou nebo potvrzovacím dialogem.
     * @param url Url ke stažení dokumentu
     */
    prepareDownload(url: string): Promise<boolean>;

    /**
     * Vrací ze serveru {@link IEnumType} na základě parametru `enumName`.
     * Pokud jméno enumu neexistuje, aplikace zaloguje chybu a vrací undefined.
     * @param enumName slouží k vyhledání typu enumu, který má metoda vrátit
     */
    getEnumData(enumName: string): Promise<IEnumType>;

    /**
     * Vrací manager data eventů. Pokud není žádný manager zaregistrovaný, vrací undefined.
     */
    getDataEventsManager(): IDataEventsManager | undefined;

    /**
     * Zjistí jestli url a funkce obsahují pouze statické metody
     * @param url Šablona url adresy
     * @param functions Pole názvů funkcí
    */
    canCallOnlyStaticMethods(url: string, functions: ReadonlyArray<string>): boolean;

    /**
     * Provede akce WorkFlow nad seznamem objektů
     * @param ids identifikátory objektů
     * @param className třída objektů
     * @param actionID identifikátor spouštěče
     * @param nextParticipants
     * @param requestContext Viz {@link RequestContext}
     */
    objectWFActionExecuteWithoutInstance(ids: string[], className: string, actionID: string, nextParticipants: IParticipant[], requestContext?: RequestContext): Promise<IRunMethodResult>;
}

/**
 * Definuje obecné funkce pro práci související s uživatelským rozhraním.
 */
export interface IBridgeUI {

    /**
     * Zastaví propagaci události, ale současně notifikuje o tomto zastavení (vyvolává nativní událost "<název události>Cancelled")
     *
     * @param event Událost, která má být zastavena.
     */
    stopPropagation(event: SyntheticEvent): void;

    /**
     * Zastaví propagaci události, ale současně notifikuje o tomto zastavení (vyvolává nativní událost "<název události>Cancelled")
     *
     * @param event Událost, která má být zastavena.
     */
    stopPropagationNative(event: Event): void;

    /**
     * Vygeneruje id použitelné pro elementy komponent.
     */
    generateElementID(): string;

    /**
     * @see UserActionLogger.logEvent
     * @param event Událost k zalogování uživatelské aktivity.
     */
    logUserInteractionEvent(event: Event): Promise<void>;

    /**
     * Vrací viditelnost komponenty dané identifikátorem.
     * Pokud komponenta nebyla nalezena nebo neimplementuje rozhraní IVisible, vrací **true**.
     *
     * @param componentId Identifikátor komponenty, pro kterou vrací její viditelnost.
     */
    getControlComponentVisibility(componentId: string): boolean;

    /**
     * Přidá listener na změnu viditelnosti komponentě dané identifikátorem.
     *
     * @param componentId Identifikátor komponenty, které nastavuje listener.
     * @param listener Listener, který přidá do komponenty dané identifikátorem.
     */
    addControlComponentVisibilityChangedListener(componentId: string, listener: EventCallback<IVisible, boolean>): void;

    /**
     * Odebere Listener na změnu viditelnosti komponentě dané identifikátorem.
     *
     * @param componentId Identifikátor komponenty, které nastavuje listener.
     * @param listener Listener, který odebere z komponenty dané identifikátorem.
     */
    removeControlComponentVisibilityChangedListener(componentId: string, listener: EventCallback<IVisible, boolean>): void;
}

/**
 * Poskytuje rozhraní pro formátování různých datových typů a pro práci s řetězci.
 */
export interface IBridgeFormatting {

    /** Znak pro oddělování desetinných míst */
    readonly decimalSeparator: string;

    /** Znak pro oddělování skupin znaků v čísle */
    readonly groupSeparator: string;

    /** Počet milisekund v jednom dni */
    readonly msPerDay: number;

    /**
     * Odstraní diakritiku z textu.
     * @param text Text, ze kterého se odstraní diakritika.
     */
    removeAccents(text: string): string;

    /**
     * Odstraní nebo nahradí HTML tagy v textu.
     * @param text Text, ve kterém se odstraní nebo nahradí HTML tagy.
     */
    stripHTMLFromText(text: string): string;

    /**
     * Zjistí, zda je zadané datum datem dnešním.
     * @param date Datum
     */
    isToday(date: Date): boolean;

    /**
     * Převede datum na řetězec.
     * @param date Datum
     * @param format Formát datumu. Výchozí hodnota je "g".
     */
    dateToString(date: Date, format?: string): string;

    /** @see UI/utils */
    getExtendedPropertiesFromElement(element: HTMLElement): ExtendedPropertiesType;

    /** @see NumberUtils.getDefaultFormat */
    getDefaultNumberFormat(dataType?: OWDataType | string): string;

    /** @see NumberUtils.getFormattedString */
    getFormattedNumberString(value: string | IBigNumber | number | null | undefined, params: GetFormattedNumberStringParams, formatedProviderKey?: string): string;

    /** @see NumberUtils.isWholeNumberType */
    isWholeNumberType(dataType: OWDataType | string): boolean;

    /** @see NumberUtils.getDefaultFormat */
    getDefaultFormat(dataType?: OWDataType | string): string;

    /** @see NumberUtils.getTypeMinMax */
    getTypeMinMax(dataType: OWDataType | string): Record<"min" | "max", number> | null;

    /** @see NumberUtils.numberToString */
    getNumberToString(numStr: string, params: INumberFormatOptions): string;

    /** @see StringUtils.isNullOrEmpty */
    isNullOrEmpty(value: string | null | undefined): boolean;

    /** @see StringUtils.compareCaseInsensitive */
    compareCaseInsensitive(value1: string | null | undefined, value2: string | null | undefined): boolean;

    /** @see EnumUtils.getEnumValueFromValue */
    stringToEnumValue<T>(targetEnum: T, value: string, caseSensitive?: boolean): T[keyof T] | undefined;

    /** @see FlagUtils.flagToCss */
    flagToCss(flag: string): string;

    /** @see EnumUtils.getEnumValueFromValue */
    valueToEnumValue<T>(targetEnum: T, value: string, caseSensitive?: boolean): T[keyof T] | undefined;

    /**
     * Načtení formátovací funkce
     * @param format Formát
     * @param culture Nepovinný parametr culture, který určuje, zda bude pro tento konkrétní formát použita jiná kultura než globální
     */
    getDateToStringFunction(format: string, culture?: string): DateTimeFormatObject;

    /**
     * Konvertuje danou hodnotu na boolean, pokud neodpovida hodnote.
     * @param value Obsahuje hodnotu pro parsovani.
     * @param defaultReturn Obsahuje defaultni navratovou hodnotu v pripade ze neprojde parsovanim.
     */
    stringToBoolean(value: string | null | undefined, defaultReturn?: boolean): boolean;

    /**
     * Konvertuje string na číslo. Pokud je hodnota undefined vrací místo NaN vrací undefined.
     * @param value Obsahuje hodnotu pro parsování.
     */
    stringToNumberOrUndefined(value: string | undefined): number | undefined;

    /**
     * Odstraní formátování a vrátí string s číslem v neutrální kultuře
     * @param value Hodnota, ze které má být odstraněno formátování
     * @param groupSeparator Oddělovač tisíců
     * @param decimalSeparator Oddělovač desetinných míst
     */
    removeFormatting(value: string, groupSeparator: string, decimalSeparator: string): string;

    /**
     * Testuje zda je řetězec url adresa.
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o url adresu.
     */
    isUrl(value: string): boolean;

    /**
     * Funkce testuje zda je url parametrická
     * @param url Testovaná url
     * @returns {boolean} - Vrací true v případě, že jde o parametrickou url (obsahuje @ a nejedná se o email)
     */
    isParametricUrl(url: string): boolean;

    /**
     * Testuje zda je řetězec emailová adresa.
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o emailovou adresu.
     */
    isEmail(value: string): boolean;

    /**
     * Testuje zda je řetězec parametrická emailová adresa s "mailto".
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o parametrickou emailovou adresu s "mailto".
     */
    isParametricMailto(value: string): boolean;

    /**
     * Testuje zda je řetězec cesta k souboru.
     * @param value Řetězec k otestování.
     * @returns {boolean} - Vrací true pokud se jedná o cestu k souboru.
     */
    isFile(value: string): boolean;

    /**
     * Funkce testuje zda adresa obsahuje parametry
     * @param url Testovaná url
     * @returns {boolean} - Vrací true v případě, že adresa obsahuje parametry
     */
    hasParameters(url: string): boolean;

    /**
     * Projde celý řetězec a nahradí všechny výskyty hledaného řetězce za zadanou hodnotu.
     * @param text Vstupní řetězec
     * @param searchValue Hledaná hodnota nebo výraz
     * @param replaceValue Požadovaná hodnota pro nahrazení
     */
    replaceAll(text: string, searchValue: string | RegExp, replaceValue: string): string;

    /**
     * Funkce projde celý text a nahradí hledaný řetězec požadovanou hodnotou, ale pouze v případě, že před hledaným
     * řetězcem není zakázaný řetězec. V takovém případě vrací původní text.
     * @param text Vstupní text
     * @param searchedString Hledaný řetězec
     * @param value Požadovaná hodnota pro nahrazení
     * @param forbiddenString Zakázaný řetězec
     * @returns Vrací upravený vstupní řetězec nebo původní řetězec.
     */
    replaceAllWithLookahead(text: string, searchedString: string, value: string, forbiddenString: string): string;

    /**
     * Vrací instanci DefferedWindow, pro korektní
     * otevírání stránek z async callu v Safari
     */
    createDefferedWindow(): IDefferredWindow;
}

/**
 * Definuje rozhraní pro práci s lokalizacemi. Toto rozhraní by se ve většině případů nemělo používat mimo modul core/i18n.
 */
export interface IBridgeLocalization {

    /**
     * Načte lokalizační data pro zadané klíče. Tato funkce by se v reactí části neměla volat přímo,
     * ale měl by se použít core modul i18n. Tato funkce je vhodná pouze pro případy,
     * kdy je potřeba lokalizovat text v nereactím kódu, který se ale nachází ve složkách pro react,
     * například editor WF, editor formulářů...
     * @param locKeys Lokalizační klíče, které se mají načíst.
     */
    loadLocalizationItems(locKeys: readonly string[]): Promise<Map<string, ILocalizationItem>>;

    /**
     * Aplikuje parametry na lokalizovaný řetězec.
     * @param text Lokalizovaný text s případnými placeholdery (např. `@0`).
     * @param params Parametry, které budou dosazeny za placeholdery.
     */
    applyLocParams(text: string, params?: string[]): string;

    /**
     * Vrací kulturu ve standardu ISO 639-1, tedy dvoupísmenný formát, např. en, cs, ...
     */
    readonly actualCultureISO639_1: string;

    /**
     * Načte seznam podporovaných jazyků aplikace.
     */
    getSupportedLanguages(): Promise<readonly ILanguageExtended[]>;

    /** Vrací informace o formátování čísla v aktuální kultuře. */
    getNumberFormat(): INumberFormatInfo;
}

/**
 * Definuje rozhraní pro podporu frames modulu.
 */
export interface IBridgeFrames {

    /**
     * Funkce, která provede akci na daném konektoru.
     *
     * @param dataSourceKey Identifikátor konektoru.
     * @param actionId Identifikátor akce.
     * @param dataObjectId Identifikátor záznamu.
     */
    executeReactConnectorAction(dataSourceKey: string, actionId: ReactActionId, dataObjectId: string): void;

    /**
     * Funkce, která otevře detail záznamu na daném konektoru.
     *
     * @param dataSourceKey Identifikátor konektoru.
     * @param dataObjectId Identifikátor záznamu.
     */
    openReactConnectorDetail(dataSourceKey: string, dataObjectId: string): Promise<void>;

    /**
     * Funkce, která otevře editační okno daného atributu v záznamu na daném konektoru.
     *
     * @param params Typ s informacemi potřebnými k otevření editačního okna.
     */
    openAttributeEditWindow(params: OpenEditWindowParams): Promise<void>;

    /**
     * Funkce, která nastavuje režim výběru více záznamů konektoru.
     *
     * @param dataSourceKey Identifikátor konektoru.
     * @param multiselectMode Značí, zda je aktivní režim výběru více záznamů.
     */
    setReactConnectorMultiselectMode(dataSourceKey: string, multiselectMode: boolean): void;

    /**
     * Funkce, která vrací pole akcí z konektoru.
     *
     * @param dataSourceKey Identifikátor konektoru.
     */
    getReactConnectorRowActions(dataSourceKey: string): ReadonlyArray<ReactAction>;

    /**
     * Funkce, která resetuje timeout na AutoRefresheru.
     * @param dataSourceKey Identifikátor konektoru.
     */
    resetRefreshTimeout(dataSourceKey: string): void;
}

/**
 * Definuje rozhraní poskytující funkcionalitu specifickou pro hlavní menu.
 */
export interface IBridgeMainMenu {

    /**
     * Získá nastavení pro customizaci navigace v hlavním menu. Pokud není žádné nastavení zaregistrované, vrací undefined.
     */
    getMenuNavigationConfig(): IMenuNavigationConfig | undefined;
}

/**
 * Definuje rozhraní poskytující funkcionalitu specifickou pro práci se soubory.
 */
export interface IBridgeFile {
    /**
     * Název souboru rozdělí na název a příponu
     * @param fileName název souboru k rozdělení
     */
    splitFileName(fileName: string): FileNameParts;

    /**
     * Uživatelsky přívětivě naformátuje velikost souboru v bytech
     * @param fileSize Velikost souboru v bytech
     * @param locale Kultura pro formátování čísla
     */
    prettyPrintFileSize(fileSize: number, locale?: string | string[]): string;
}

/**
 * Definuje rozhraní poskytující funkcionalitu specifickou pro práci s kryptografií.
 */
export interface IBridgeCrypto {
    /**
     * Metoda vrátí true, pokud je podpis prázdný nebo neexistuje
     * @param signatureInfo Informace o podpisu
     */
    isSignatureEmpty(signatureInfo: ISignatureInfo | null | undefined): boolean;

    /**
     * Metoda vrátí true, pokud podpis není prázdný
     * @param signatureInfo Informace o podpisu
     */
    isSigned(signatureInfo: ISignatureInfo | null | undefined): boolean;

    /**
     * Metoda vrátí true, pokud je záznam šifrován
     * @param cryptoInfo Informace o šifrování a podpisech
     */
    isEncrypted(cryptoInfo: ICryptographicInfo | null | undefined): boolean;

    /**
     * Metoda vrací true, pokud podpis není plně validní nebo se nedá korektně ověřit
     * @param signatureInfo Informace o podpisu
     * @param withCounterSignatures Rozlišuje, jestli se má zkontrolovat první nebo poslední podpis
     */
    hasSignatureWarning(signatureInfo: ISignatureInfo | null | undefined, withCounterSignatures?: boolean): boolean;
}
